import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { getErrorMessage, Alert, AlertDescription, Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch, useToast, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@nimbitlabs/ui';

interface ServiceMonitoringConfig {
  id: string;
  serviceEnvironmentId: string;
  serviceId: string;
  serviceName: string;
  environmentId: string;
  environmentName: string;
  url: string;
  method: string;
  timeoutSeconds: number;
  intervalMinutes: number;
  expectedStatusCode: number;
  expectedResponseBody?: string;
  expectedResponseHeader?: string;
  authType: string;
  authHeaderName?: string;
  retryCount: number;
  retryDelaySeconds: number;
  failureThreshold: number;
  successThreshold: number;
  enableAlerts: boolean;
  alertCooldownMinutes: number;
  isEnabled: boolean;
}

interface EditMonitoringConfigModalProps {
  open: boolean;
  config: ServiceMonitoringConfig;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditMonitoringConfigModal: React.FC<EditMonitoringConfigModalProps> = ({
  open,
  config,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    url: config.url,
    method: config.method,
    timeoutSeconds: config.timeoutSeconds,
    intervalMinutes: config.intervalMinutes,
    expectedStatusCode: config.expectedStatusCode,
    expectedResponseBody: config.expectedResponseBody || '',
    expectedResponseHeader: config.expectedResponseHeader || '',
    authType: config.authType,
    authHeaderName: config.authHeaderName || '',
    authHeaderValue: '',
    retryCount: config.retryCount,
    retryDelaySeconds: config.retryDelaySeconds,
    failureThreshold: config.failureThreshold,
    successThreshold: config.successThreshold,
    enableAlerts: config.enableAlerts,
    alertCooldownMinutes: config.alertCooldownMinutes,
    isEnabled: config.isEnabled,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        url: config.url,
        method: config.method,
        timeoutSeconds: config.timeoutSeconds,
        intervalMinutes: config.intervalMinutes,
        expectedStatusCode: config.expectedStatusCode,
        expectedResponseBody: config.expectedResponseBody || '',
        expectedResponseHeader: config.expectedResponseHeader || '',
        authType: config.authType,
        authHeaderName: config.authHeaderName || '',
        authHeaderValue: '',
        retryCount: config.retryCount,
        retryDelaySeconds: config.retryDelaySeconds,
        failureThreshold: config.failureThreshold,
        successThreshold: config.successThreshold,
        enableAlerts: config.enableAlerts,
        alertCooldownMinutes: config.alertCooldownMinutes,
        isEnabled: config.isEnabled,
      });
    }
  }, [open, config]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.url || !formData.url.trim()) {
      setError('Please enter a health check URL');
      return;
    }
    
    if (formData.url.length > 2000) {
      setError('Health check URL must be 2000 characters or less');
      return;
    }
    
    // Validate URL format
    try {
      new URL(formData.url);
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com/health)');
      return;
    }
    
    if (!formData.method) {
      setError('Please select an HTTP method');
      return;
    }
    
    if (formData.timeoutSeconds < 1 || formData.timeoutSeconds > 300) {
      setError('Timeout must be between 1 and 300 seconds');
      return;
    }
    
    if (formData.intervalMinutes < 1) {
      setError('Check interval must be at least 1 minute');
      return;
    }
    
    if (formData.expectedStatusCode < 100 || formData.expectedStatusCode > 599) {
      setError('Expected status code must be between 100 and 599');
      return;
    }

    const updateData = {
      url: formData.url,
      method: formData.method as any,
      timeoutSeconds: formData.timeoutSeconds,
      intervalMinutes: formData.intervalMinutes,
      expectedStatusCode: formData.expectedStatusCode,
      expectedResponseBody: formData.expectedResponseBody || null,
      expectedResponseHeader: formData.expectedResponseHeader || null,
      authType: formData.authType as any,
      authHeaderName: formData.authHeaderName || null,
      authHeaderValue: formData.authHeaderValue || null,
      retryCount: formData.retryCount,
      retryDelaySeconds: formData.retryDelaySeconds,
      failureThreshold: formData.failureThreshold,
      successThreshold: formData.successThreshold,
      enableAlerts: formData.enableAlerts,
      alertCooldownMinutes: formData.alertCooldownMinutes,
      isEnabled: formData.isEnabled,
    };

    console.log('Updating monitoring config:', config.id, updateData);

    try {
      setLoading(true);
      const result = await api.serviceMonitoringConfigurations.update(config.id, updateData);
      console.log('Update successful:', result);

      toast({
        title: 'Success',
        description: 'Monitoring updated successfully',
      });
      onSuccess();
    } catch (error: any) {
      console.error('Failed to update monitoring configuration:', error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Monitoring</DialogTitle>
          <DialogDescription>
            {config.serviceName} - {config.environmentName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* HTTP Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">HTTP Configuration</h3>
            
            <div className="space-y-2">
              <Label htmlFor="url">Health Check URL *</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://api.example.com/health"
                required
                maxLength={2000}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="method">HTTP Method</Label>
                <Select
                  value={formData.method}
                  onValueChange={(value) => setFormData({ ...formData, method: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="HEAD">HEAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectedStatusCode">Expected Status Code</Label>
                <Input
                  id="expectedStatusCode"
                  type="number"
                  value={formData.expectedStatusCode}
                  onChange={(e) => setFormData({ ...formData, expectedStatusCode: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expectedResponseBody">Expected Response Body (optional)</Label>
              <Input
                id="expectedResponseBody"
                value={formData.expectedResponseBody}
                onChange={(e) => setFormData({ ...formData, expectedResponseBody: e.target.value })}
                placeholder="e.g., 'OK' or 'healthy'"
              />
            </div>
          </div>

          {/* Timing Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Timing</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="intervalMinutes">Check Interval</Label>
                <Select
                  value={formData.intervalMinutes.toString()}
                  onValueChange={(value) => setFormData({ ...formData, intervalMinutes: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="10">10 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">60 minutes</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">How often to check (minimum 5 minutes)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeoutSeconds">Timeout (seconds)</Label>
                <Input
                  id="timeoutSeconds"
                  type="number"
                  value={formData.timeoutSeconds}
                  onChange={(e) => setFormData({ ...formData, timeoutSeconds: parseInt(e.target.value) })}
                  min="1"
                />
                <p className="text-xs text-muted-foreground">Max wait time for response</p>
              </div>
            </div>
          </div>

          {/* Retry Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Retry & Thresholds</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="retryCount">Retry Count</Label>
                <Input
                  id="retryCount"
                  type="number"
                  value={formData.retryCount}
                  onChange={(e) => setFormData({ ...formData, retryCount: parseInt(e.target.value) })}
                  min="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="retryDelaySeconds">Retry Delay (seconds)</Label>
                <Input
                  id="retryDelaySeconds"
                  type="number"
                  value={formData.retryDelaySeconds}
                  onChange={(e) => setFormData({ ...formData, retryDelaySeconds: parseInt(e.target.value) })}
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="failureThreshold">Failure Threshold</Label>
                <Input
                  id="failureThreshold"
                  type="number"
                  value={formData.failureThreshold}
                  onChange={(e) => setFormData({ ...formData, failureThreshold: parseInt(e.target.value) })}
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="successThreshold">Success Threshold</Label>
                <Input
                  id="successThreshold"
                  type="number"
                  value={formData.successThreshold}
                  onChange={(e) => setFormData({ ...formData, successThreshold: parseInt(e.target.value) })}
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Authentication */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Authentication</h3>
            
            <div className="space-y-2">
              <Label htmlFor="authType">Auth Type</Label>
              <Select
                value={formData.authType}
                onValueChange={(value) => setFormData({ ...formData, authType: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="None">None</SelectItem>
                  <SelectItem value="Bearer">Bearer Token</SelectItem>
                  <SelectItem value="ApiKey">API Key</SelectItem>
                  <SelectItem value="Basic">Basic Auth</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.authType !== 'None' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="authHeaderName">Header Name</Label>
                  <Input
                    id="authHeaderName"
                    value={formData.authHeaderName}
                    onChange={(e) => setFormData({ ...formData, authHeaderName: e.target.value })}
                    placeholder="e.g., Authorization, X-API-Key"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="authHeaderValue">Header Value</Label>
                  <Input
                    id="authHeaderValue"
                    type="password"
                    value={formData.authHeaderValue}
                    onChange={(e) => setFormData({ ...formData, authHeaderValue: e.target.value })}
                    placeholder="Leave empty to keep existing value"
                  />
                  <p className="text-xs text-muted-foreground">Leave empty to keep the existing auth value</p>
                </div>
              </>
            )}
          </div>

          {/* Alert Configuration */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Alerts</h3>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="enableAlerts">Enable Alerts</Label>
                <p className="text-sm text-muted-foreground">Trigger alerts when health checks fail</p>
              </div>
              <Switch
                id="enableAlerts"
                checked={formData.enableAlerts}
                onCheckedChange={(checked) => setFormData({ ...formData, enableAlerts: checked })}
              />
            </div>

            {formData.enableAlerts && (
              <div className="space-y-2">
                <Label htmlFor="alertCooldownMinutes">Alert Cooldown (minutes)</Label>
                <Input
                  id="alertCooldownMinutes"
                  type="number"
                  value={formData.alertCooldownMinutes}
                  onChange={(e) => setFormData({ ...formData, alertCooldownMinutes: parseInt(e.target.value) })}
                  min="1"
                />
              </div>
            )}
          </div>

          {/* Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Status</h3>
            
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="isEnabled">Monitoring Enabled</Label>
                <p className="text-sm text-muted-foreground">Enable or disable this monitoring</p>
              </div>
              <Switch
                id="isEnabled"
                checked={formData.isEnabled}
                onCheckedChange={(checked) => setFormData({ ...formData, isEnabled: checked })}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Monitoring'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
